import { useNavigate } from 'react-router-dom'
import { useProtocol } from '@/hooks/useProtocol'
import { useViewProfile } from '@/hooks/useViewProfile'
import { useAccountUnreadCount, formatUnreadBadgeCount } from '@/hooks/useAccountUnreadCount'
import { Avatar } from '@/components/Avatar'
import { Check, X } from 'lucide-react'
import { strings } from '@/strings'
import type { ConnectedAccount } from '@/types/account'

interface AccountSwitcherProps {
    onSelect?: () => void
    onRemove?: (account: ConnectedAccount) => void
    /** Called when an expired placeholder row is tapped. */
    onReconnect?: (account: ConnectedAccount) => void
}

function AccountRow({
    account,
    isActive,
    onSelect,
    onRemove,
}: {
    account: ConnectedAccount
    isActive: boolean
    onSelect: () => void
    onRemove?: (account: ConnectedAccount) => void
}) {
    // For Bluesky accounts, fetch fresh profile data (deduped by React Query)
    const identifier = account.platform === 'bluesky' ? account.id : undefined
    const { profile } = useViewProfile(identifier)

    // Empty strings from a freshly-fetched profile should fall back to the cached account
    // data, not render blank.
    /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
    const displayName = profile?.displayName || account.displayName
    const handle = profile?.handle || account.handle
    const avatar = profile?.avatar || account.avatar
    /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

    const isExpired = account.status === 'expired'
    const unread = useAccountUnreadCount({ account, isActive })
    const pillText = formatUnreadBadgeCount(unread.count)
    const unreadAriaLabel =
        !isExpired && pillText
            ? strings.login.accountWithUnreadAriaLabel(displayName, pillText)
            : undefined

    return (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-border-light/30 dark:hover:bg-white/10">
            <button
                onClick={onSelect}
                className="flex items-center gap-3 min-w-0 flex-1"
                aria-label={isExpired ? strings.login.reconnectAriaLabel(handle) : unreadAriaLabel}
            >
                <div className={`relative ${isExpired ? 'opacity-40' : ''}`}>
                    <Avatar src={avatar} alt={displayName} size="sm" />
                    {pillText && !isExpired && (
                        <span
                            aria-hidden="true"
                            className="absolute -top-1 -right-1.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1"
                        >
                            {pillText}
                        </span>
                    )}
                </div>
                <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark truncate max-w-full">
                        {displayName}
                    </span>
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate max-w-full">
                        {isExpired ? strings.login.expiredSubtitle : `@${handle}`}
                    </span>
                </div>
                {/* An expired placeholder is never the active account */}
                {isActive && !isExpired && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
            </button>
            {onRemove && (
                <button
                    onClick={e => {
                        e.stopPropagation()
                        onRemove(account)
                    }}
                    className="p-1 rounded-md text-text-secondary-light dark:text-text-secondary-dark hover:text-red-500 hover:bg-red-500/10 transition-colors flex-shrink-0"
                    aria-label={`Remove ${displayName}`}
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    )
}

export function AccountSwitcher({ onSelect, onRemove, onReconnect }: AccountSwitcherProps) {
    const { connectedAccounts, activeAccount, setActiveAccount } = useProtocol()
    const navigate = useNavigate()

    return (
        <div className="flex flex-col gap-0.5">
            {connectedAccounts.map(account => (
                <AccountRow
                    key={account.id}
                    account={account}
                    isActive={activeAccount?.id === account.id}
                    onSelect={() => {
                        if (account.status === 'expired') {
                            // Tapping a placeholder reconnects it — it never becomes active
                            onReconnect?.(account)
                            return
                        }
                        if (activeAccount?.id !== account.id) {
                            setActiveAccount(account.id)
                            void navigate('/', { replace: true })
                        }
                        onSelect?.()
                    }}
                    onRemove={onRemove}
                />
            ))}
        </div>
    )
}
